<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import AqueductMaterialViewport from '$components/aqueduct/AqueductMaterialViewport.svelte';
	import AqueductViewportLoading from '$components/aqueduct/AqueductViewportLoading.svelte';
	import WebGpuWarning from '$components/aqueduct/WebGpuWarning.svelte';
	import { AqueductSession } from '$lib/aqueduct/engine/session';
	import type { ProjectHandle } from '$lib/aqueduct/engine/projectHandle';
	import { DEFAULT_MATERIAL_DISPLACEMENT_SCALE } from '$lib/aqueduct/materialRendererDefaults';
	import type { MaterialViewMode } from '$lib/aqueduct/materialViewMode';
	import { getDefaultMaterialGridSsaaScale } from '$lib/surfaceDpr';

	type MaterialRole = 'albedo' | 'height' | 'normal' | 'roughness' | 'metallic' | 'ao' | 'opacity';
	type SsaaScale = 1 | 2 | 3 | 4 | 6 | 8;
	type LegacyMapType =
		| 'map'
		| 'displacementMap'
		| 'normalMap'
		| 'metalnessMap'
		| 'roughnessMap'
		| 'aoMap'
		| 'opacityMap';

	let {
		docId,
		clientId = docId,
		viewMode,
		isDark,
		displacementScale = DEFAULT_MATERIAL_DISPLACEMENT_SCALE,
		ssaaScale = getDefaultMaterialGridSsaaScale(),
		enableMomentum = true,
		warningDescription = 'This material viewport requires WebGPU to preview materials on your GPU.',
		colorMap = $bindable(null),
		heightMap = $bindable(null),
		normalMap = $bindable(null),
		metallicMap = $bindable(null),
		roughnessMap = $bindable(null),
		aoMap = $bindable(null),
		opacityMap = $bindable(null)
	}: {
		docId: string;
		clientId?: string;
		viewMode: MaterialViewMode;
		isDark: boolean;
		displacementScale?: number;
		ssaaScale?: SsaaScale;
		enableMomentum?: boolean;
		warningDescription?: string;
		colorMap?: HTMLImageElement | null;
		heightMap?: HTMLImageElement | null;
		normalMap?: HTMLImageElement | null;
		metallicMap?: HTMLImageElement | null;
		roughnessMap?: HTMLImageElement | null;
		aoMap?: HTMLImageElement | null;
		opacityMap?: HTMLImageElement | null;
	} = $props();

	let materialViewport: AqueductMaterialViewport = $state(undefined!);
	let session: AqueductSession | null = null;
	let wasmProject: ProjectHandle | null = $state(null);
	let webGpuChecked = $state(false);
	let webGpuAvailable = $state(false);
	let materialViewportError = $state('');
	let pendingExternalImages: Map<MaterialRole, HTMLImageElement> = new Map();

	function normalizeRole(roleOrMapType: MaterialRole | LegacyMapType): MaterialRole {
		switch (roleOrMapType) {
			case 'map':
				return 'albedo';
			case 'displacementMap':
				return 'height';
			case 'normalMap':
				return 'normal';
			case 'metalnessMap':
				return 'metallic';
			case 'roughnessMap':
				return 'roughness';
			case 'opacityMap':
				return 'opacity';
			case 'aoMap':
				return 'ao';
			default:
				return roleOrMapType;
		}
	}

	function setBoundImage(role: MaterialRole, image: HTMLImageElement | null): void {
		switch (role) {
			case 'albedo':
				colorMap = image;
				break;
			case 'height':
				heightMap = image;
				break;
			case 'normal':
				normalMap = image;
				break;
			case 'metallic':
				metallicMap = image;
				break;
			case 'roughness':
				roughnessMap = image;
				break;
			case 'opacity':
				opacityMap = image;
				break;
			case 'ao':
				aoMap = image;
				break;
		}
	}

	function flushPendingExternalImages(): void {
		if (!materialViewport || pendingExternalImages.size === 0) return;
		for (const [role, image] of Array.from(pendingExternalImages.entries())) {
			materialViewport.updateMaterialMap(role, image);
			pendingExternalImages.delete(role);
		}
	}

	function initMaterialViewport(): void {
		try {
			// Session creation is synchronous: work buffers behind the worker's
			// acquire/open messages, so no readiness gating is needed.
			session = new AqueductSession({ docId, clientId, warmSlangCompiler: false });
			const handle = session.project;
			handle.setMaterialViewportSourceMode('external-textures');
			wasmProject = handle;
		} catch (error) {
			materialViewportError = error instanceof Error ? error.message : String(error);
			console.error('Failed to initialize standalone Aqueduct material viewport:', error);
		}
	}

	export function updateMaterialMap(
		roleOrMapType: MaterialRole | LegacyMapType,
		image: HTMLImageElement
	): void {
		const role = normalizeRole(roleOrMapType);
		setBoundImage(role, image);
		if (!materialViewport) {
			pendingExternalImages.set(role, image);
			return;
		}
		materialViewport.updateMaterialMap(role, image);
	}

	export function removeMap(roleOrMapType: MaterialRole | 'color' | 'metallic'): void {
		const role =
			roleOrMapType === 'color'
				? 'albedo'
				: roleOrMapType === 'metallic'
					? 'metallic'
					: normalizeRole(roleOrMapType as MaterialRole | LegacyMapType);
		pendingExternalImages.delete(role);
		setBoundImage(role, null);
		materialViewport?.removeMap(role);
	}

	export function clearPBRMaps(): void {
		for (const role of [
			'height',
			'normal',
			'metallic',
			'roughness',
			'ao',
			'opacity'
		] as MaterialRole[]) {
			removeMap(role);
		}
	}

	onMount(() => {
		webGpuAvailable = Boolean(navigator.gpu);
		webGpuChecked = true;
		if (webGpuAvailable) initMaterialViewport();
	});

	onDestroy(() => {
		session?.close();
		session = null;
	});

	$effect(() => {
		if (materialViewport) flushPendingExternalImages();
	});
</script>

{#if webGpuAvailable && wasmProject && !materialViewportError}
	<AqueductMaterialViewport
		bind:this={materialViewport}
		bind:colorMap
		bind:heightMap
		bind:normalMap
		bind:metallicMap
		bind:roughnessMap
		bind:opacityMap
		bind:aoMap
		{wasmProject}
		sourceMode="external-textures"
		{viewMode}
		{isDark}
		{displacementScale}
		{ssaaScale}
		{enableMomentum}
	/>
{:else if !webGpuChecked || (webGpuAvailable && !materialViewportError)}
	<AqueductViewportLoading quiet />
{:else}
	<div
		class="absolute left-1/2 top-1/2 z-10 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2"
	>
		<WebGpuWarning
			title="WebGPU required"
			description={materialViewportError || warningDescription}
		/>
	</div>
{/if}
